import React, { useMemo, useState } from 'react';
import { Cloud, CloudOff, WifiOff } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { cn } from '@/shared/utils/utils';
import type { User } from '../../services/websocketService';

interface ViewerPresenceProps {
  activeUsers: User[];
  isConnected: boolean;
  isOffline: boolean;
}

// Deterministic per-user color so each avatar stays the same hue across renders.
const USER_PALETTE = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-fuchsia-500',
  'bg-teal-500',
  'bg-orange-500',
];

const colorForUser = (userId: number): string => {
  const index = Math.abs(userId) % USER_PALETTE.length;
  return USER_PALETTE[index];
};

const displayName = (user: User): string => {
  const full = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return full || user.email || `User ${user.id}`;
};

const initials = (user: User): string => {
  const first = (user.first_name ?? '').trim();
  const last = (user.last_name ?? '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
  }
  const email = (user.email ?? '').trim();
  if (email) return email.charAt(0).toUpperCase();
  return '?';
};

export const ViewerPresence: React.FC<ViewerPresenceProps> = ({
  activeUsers,
  isConnected,
  isOffline,
}) => {
  const [open, setOpen] = useState(false);

  const sortedUsers = useMemo(
    () => activeUsers.slice().sort((a, b) => displayName(a).localeCompare(displayName(b))),
    [activeUsers]
  );

  const visible = sortedUsers.slice(0, 4);
  const overflow = Math.max(0, sortedUsers.length - visible.length);

  const statusLabel = isOffline
    ? 'Offline'
    : !isConnected
      ? 'Connecting…'
      : sortedUsers.length === 0
        ? 'Live · no one else here'
        : `Live · ${sortedUsers.length} viewer${sortedUsers.length === 1 ? '' : 's'}`;

  const StatusIcon = isOffline ? WifiOff : isConnected ? Cloud : CloudOff;
  const statusTone = isOffline
    ? 'text-muted-foreground'
    : isConnected
      ? 'text-emerald-600'
      : 'text-amber-600';

  return (
    <TooltipProvider delayDuration={200}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full border bg-background/95 backdrop-blur px-2 py-1 shadow-sm hover:bg-accent transition-colors"
          >
            <StatusIcon className={cn('h-3.5 w-3.5', statusTone)} />
            <div className="flex -space-x-1.5">
              {visible.map((user) => (
                <Tooltip key={user.id}>
                  <TooltipTrigger asChild>
                    <Avatar className="h-6 w-6 ring-2 ring-background">
                      <AvatarFallback
                        className={cn(
                          'text-[10px] font-semibold text-white',
                          colorForUser(user.id)
                        )}
                      >
                        {initials(user)}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {displayName(user)}
                  </TooltipContent>
                </Tooltip>
              ))}
              {overflow > 0 && (
                <Avatar className="h-6 w-6 ring-2 ring-background">
                  <AvatarFallback className="bg-muted text-foreground text-[10px] font-semibold">
                    +{overflow}
                  </AvatarFallback>
                </Avatar>
              )}
              {sortedUsers.length === 0 && (
                <span className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
                  —
                </span>
              )}
            </div>
            <span className="text-[11px] text-muted-foreground pr-1 hidden sm:inline">
              {statusLabel}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2">
          <div className="flex items-center gap-2 px-1 pb-2 border-b">
            <StatusIcon className={cn('h-4 w-4', statusTone)} />
            <span className="text-xs font-medium">{statusLabel}</span>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {sortedUsers.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground text-center">
                You&apos;re the only one viewing this drawing right now.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {sortedUsers.map((user) => (
                  <li
                    key={user.id}
                    className="flex items-center gap-2 px-1 py-1.5 rounded-md hover:bg-accent"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarFallback
                        className={cn(
                          'text-[11px] font-semibold text-white',
                          colorForUser(user.id)
                        )}
                      >
                        {initials(user)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{displayName(user)}</p>
                      {user.email && (
                        <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                      )}
                    </div>
                    <span className="h-2 w-2 rounded-full bg-emerald-500" title="Active" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
